/**
 * Заведение заявки в банк через СНС в диалоге (#436, #440) — общий сценарий для обоих
 * помощников: «Диалог по заявке» (раздел «Брокер») и «AI агент» (на телефоне — первый экран).
 *
 * Алгоритм: брокер выбирает банк (словами или кнопкой на карточке) → проверяем, подключён
 * ли банк к СНС → с согласия брокера сохраняем карточку выбора → берём заявку с
 * предзаполнением (карточка выбора + карточка клиента) → спрашиваем только недостающее →
 * документы через 📎 (у нас хранятся до окончания скоринга в СНС) → сводка → отправка
 * строго по кнопке → статус скоринга. Плюс ответы «какие банки подключены к СНС».
 *
 * Помощник даёт хосту: как показать текст и кнопки, занятость, предложения подбора,
 * параметры сделки, реплики диалога и как сохранить карточку выбора.
 */
import { useRef, useState } from "react";
import * as api from "./api";
import { BrokerOffer } from "../types";
import { formatRubles } from "./math_helpers";
import {
  isSnsSubmitIntent, isSnsListIntent, followUpBankName, findSnsTarget, lastMentionedOffer, mentionsPlatform, nextField, SNS_FIELDS, SnsField,
  isSkip, isCancel, docTypeByName, fmtDate, bulkSnsPick, isDoItIntent, isAddBanksIntent,
} from "./snsChat";
import { lawLabel } from "./law";

export type ChatAction = { label: string; run: () => void; primary?: boolean };

export interface SnsHost {
  pushText: (role: "assistant" | "user", text: string) => void;
  pushActions: (text: string, actions: ChatAction[]) => void;
  withBusy: (note: string, fn: () => Promise<void>) => Promise<void>;
  getOffers: () => BrokerOffer[];
  getDeal: () => { amount?: number | null; law?: string | null; tenderNumber?: string | null;
                   inn?: string | null };
  getSaidTexts: () => string[];
  /** Сохранить карточку выбора с этим предложением и вернуть её ref. */
  ensureCase?: (o: BrokerOffer) => Promise<string>;
  /** Сообщение со списком банков СНС и чекбоксами (#446); без него — сразу во все проходящие. */
  pushSnsPicker?: (text: string, offers: BrokerOffer[]) => void;
}

/** Блок «Через СНС» в ответе с подбором (#441): подключённые к СНС банки — первым списком. */
export type SnsBlock = {
  state: "loading" | "ok" | "off" | "error";
  offers: BrokerOffer[];      // лучшее предложение каждого подключённого банка
  note?: string;              // off/error — что сказать брокеру
};

/** Строка о документах клиента, уже лежащих в СНС (#613).
 *
 *  Нужна до сборки заявки: СНС подтягивает ранее сданные документы в новый заказ, и если
 *  не сказать об этом сразу, брокер идёт искать паспорт и устав, которые у банка есть.
 *  Справка необязательная — СНС недоступна или токена нет, возвращаем пустую строку. */
async function snsDocsNote(inn?: string | null): Promise<string> {
  const id = (inn || "").trim();
  if (!id) return "";
  try {
    const r = await api.snsClientDocuments(id);
    const names = (r.documentsInSns || []).map((d) => d.type).filter(Boolean);
    if (!names.length) return "";
    const uniq = Array.from(new Set(names as string[]));
    return `\n\nПо этому клиенту в СНС уже есть: ${uniq.join(", ")} — заново прикреплять не нужно.`;
  } catch {
    return "";
  }
}

/** Лучшее предложение банка: проходящее, затем дешевле. */
export function bestOfferOf(offers: BrokerOffer[], bank: string): BrokerOffer | undefined {
  return offers.filter((o) => o.bank === bank)
    .sort((a, b) => Number(b.eligible) - Number(a.eligible) || (a.commission || 0) - (b.commission || 0))[0];
}

const SNS_EXT_RE = /\.(pdf|jpe?g|png|tiff?|sig|p7s)$/i;   // форматы, которые принимает СНС

type SnsFlow = {
  offer: BrokerOffer; snsBankId: number; snsName: string;
  more?: { offer: BrokerOffer; snsBankId: number; snsName: string }[];   // «во все банки СНС» (#445)
  ref?: string;
  sub: Record<string, any>;
  products: Record<string, string>;
  asked: Set<string>;            // поля, уже спрошенные в этом проходе
  editQueue?: SnsField[];        // «Исправить данные»: переспрашиваем по очереди
  field?: SnsField;
  docs: { id?: number; file_name: string; doc_type?: string | null; meta?: { year?: number | null; quarter?: number | null } | null }[];
  /** Коды документов, которые у клиента уже лежат в СНС: она подтянет их в новый заказ,
   *  и просить их у брокера — отправлять его искать то, что у банка есть (#613). */
  inSns: string[];
  docLabels: Record<string, string>;   // подписи типов документов — с бэкенда, не дублируем
  docMatch: Record<string, string[]>;  // и правила распознавания типа по имени файла (#614)
  askedDocs: boolean;
  pendingFile?: File;            // отчётность ждёт года и периода
  backStep?: SnsFlow["step"];    // куда вернуться после ввода периода
  step: "offer" | "field" | "passport" | "docs" | "docmeta" | "confirm" | "sent";
};

export function useSnsFlow(host: SnsHost) {
  const hostRef = useRef(host);
  hostRef.current = host;                        // хост меняется каждый рендер — берём свежий
  const H = () => hostRef.current;
  const snsRef = useRef<SnsFlow | null>(null);
  const [snsOn, setSnsOn] = useState(false);     // для accept/подсказки поля ввода
  const snsAskedRef = useRef(false);             // прошлый вопрос был про СНС — ждём «а Сбербанк?»
  const lastBankRef = useRef<string | null>(null);   // банк последнего ответа про СНС — для «причины»
  // Последняя просьба о подаче и остаток предложенных банков (#615): брокер продолжает
  // разговор короткой репликой — «так подготовь мне», «добавь ещё банки», — и условие,
  // названное шагом раньше, повторять не обязан.
  const lastSubmitTextRef = useRef<string | null>(null);
  const restOfPickRef = useRef<BrokerOffer[]>([]);
  const pickedRef = useRef<BrokerOffer[]>([]);
  // Подключение к СНС за сессию диалога — по ПРЕДЛОЖЕНИЮ (банк + площадка, #445): канал СНС —
  // площадка (ТендерТех), у банка бывают и прямые каналы. Пересортировка не дёргает СНС.
  const connCache = useRef(new Map<string, api.SnsBankMatch>());
  const keyOf = (o: { bank: string; platform?: string | null }) => `${o.bank}|${o.platform || ""}`;

  /** Статус подключения к СНС для каждого предложения (с кэшем). Бросает при сбое/нет токена. */
  const resolveOffers = async (offers: BrokerOffer[]): Promise<Map<string, api.SnsBankMatch>> => {
    const uniq = new Map<string, BrokerOffer>();
    offers.forEach((o) => { if (o.bank && !uniq.has(keyOf(o))) uniq.set(keyOf(o), o); });
    const need = Array.from(uniq.values()).filter((o) => !connCache.current.has(keyOf(o)));
    if (need.length) {
      const res = await api.snsResolve(need.map((o) => ({ bankId: o.bankId, name: o.bank, platform: o.platform || undefined })));
      res.forEach((r, i) => connCache.current.set(keyOf(need[i]), r));
    }
    return connCache.current;
  };

  /** Предложения, которые подаются через СНС: по одному лучшему на банк. */
  const snsOffersOf = (offers: BrokerOffer[], cache: Map<string, api.SnsBankMatch>): BrokerOffer[] => {
    const byBank = new Map<string, BrokerOffer>();
    for (const o of offers) {
      if (!cache.get(keyOf(o))?.connected) continue;
      const cur = byBank.get(o.bank);
      if (!cur || Number(o.eligible) > Number(cur.eligible)
          || (o.eligible === cur.eligible && (o.commission || 0) < (cur.commission || 0))) byBank.set(o.bank, o);
    }
    return Array.from(byBank.values())
      .sort((a, b) => Number(b.eligible) - Number(a.eligible) || (a.commission || 0) - (b.commission || 0));
  };

  /** Блок «Через СНС» (#441, #445): предложения подбора на СНС-площадке банков из списка СНС. */
  const resolveConnected = async (offers: BrokerOffer[]): Promise<SnsBlock> => {
    try {
      return { state: "ok", offers: snsOffersOf(offers, await resolveOffers(offers)) };
    } catch (e: any) {
      return { state: e?.detail?.code === "SNS_NOT_CONFIGURED" ? "off" : "error", offers: [], note: e?.message || String(e) };
    }
  };

  // Почему предложение не подаётся через СНС (#445): канал СНС — конкретная площадка банка
  // (ТендерТех или, как у Сбербанка, прямой канал), остальные его площадки — не СНС.
  const whyNot = (m: api.SnsBankMatch | undefined, o: { bank: string; platform?: string | null }) =>
    m?.reason === "other_platform" || m?.reason === "manual_off"
      ? `${o.bank}${o.platform ? ` на площадке ${o.platform}` : ""} через СНС не подаётся`
      : m?.reason === "no_sns_platform" ? `у ${o.bank} нет площадки, подключённой к СНС`
      : `${o.bank} не подключён к СНС`;

  // ── Заявка в банк через СНС (#436) ─────────────────────────────────────────
  // Алгоритм: брокер выбирает банк (словами или кнопкой на карточке) → проверяем, подключён
  // ли банк к СНС → с согласия брокера сохраняем карточку выбора → берём заявку с
  // предзаполнением (карточка выбора + карточка клиента) → спрашиваем только недостающее →
  // документы через 📎 (у нас хранятся до окончания скоринга в СНС) → сводка → отправка
  // строго по кнопке → статус скоринга. Контакты и реквизиты бэкенд пишет в карточку клиента.

  const snsEditable = (sub: Record<string, any>) => {
    const keys = [...SNS_FIELDS.map((f) => f.key), "product_id", "date_start", "target_bank_ids", "is_closed_tender"];
    return Object.fromEntries(keys.filter((k) => sub[k] != null && sub[k] !== "").map((k) => [k, sub[k]]));
  };

  // quiet — брокер площадку не называл: сразу берём СНС-канал банка без пояснения про другую площадку.
  const startSns = (picked: BrokerOffer, byPlatform = false, quiet = false) => H().withBusy("Проверяю подключение банка к СНС…", async () => {
    let cache: Map<string, api.SnsBankMatch>;
    const sameBank = H().getOffers().filter((x) => x.bank === picked.bank);
    try {
      cache = await resolveOffers(sameBank.length ? sameBank : [picked]);
    } catch (e: any) {
      // Нет токена СНС у организации (#439) — текст уже говорит, где его добавить.
      H().pushText("assistant", e?.detail?.code === "SNS_NOT_CONFIGURED" ? e.message
        : `Не удалось проверить подключение к СНС: ${e?.message || e}. Подать заявку можно формой «Подать в банк (СНС)» под подбором.`);
      return;
    }
    const via = byPlatform && picked.platform ? `Площадка ${picked.platform} в этом подборе — это ${picked.bank}. ` : "";
    // #445: через СНС подаётся предложение банка на СНС-площадке. Выбрали прямой канал —
    // берём СНС-предложение того же банка, если оно есть в подборе.
    let o = picked;
    let note = "";
    if (!cache.get(keyOf(picked))?.connected) {
      const alt = snsOffersOf(sameBank, cache)[0];
      if (!alt) {
        snsRef.current = null;
        H().pushText("assistant", `${via}${whyNot(cache.get(keyOf(picked)), picked)} — завести заявку через СНС нельзя.` +
          (picked.platform ? ` Подать можно на площадке ${picked.platform} напрямую.` : ""));
        return;
      }
      o = alt;
      if (!quiet) note = `${whyNot(cache.get(keyOf(picked)), picked)}; через СНС ${o.bank} подаётся на площадке ${o.platform} ` +
        `(комиссия ${formatRubles(o.commission)}${o.avRate ? `, КВ ${Math.round(o.avRate * 100)}%` : ""}). `;
    }
    const match = cache.get(keyOf(o))!;
    lastBankRef.current = o.bank;
    const reasons = o.eligible ? "" : `\n\n⚠️ По условиям банка заявка не проходит: ${(o.reasons || []).join("; ") || "причина не указана"}. ` +
      "Банк скорее всего откажет — заводите, только если уверены.";
    snsRef.current = {
      offer: o, snsBankId: match.snsBankId!, snsName: match.snsName || o.bank, sub: {}, products: {},
      asked: new Set(), docs: [], inSns: [], docLabels: {}, docMatch: {},
      askedDocs: false, step: "offer",
    };
    // Что из документов у клиента уже в СНС — говорим ДО сборки заявки (#613). Иначе
    // брокер узнаёт об этом в конце и успевает сходить за файлами, которые не нужны.
    const docsNote = await snsDocsNote(H().getDeal().inn);
    H().pushActions(
      `${via}${note}${o.bank} подключён к СНС${match.snsName && match.snsName !== o.bank ? ` («${match.snsName}»)` : ""}` +
      `${o.platform ? `, площадка ${o.platform}` : ""}. ` +
      "Могу завести заявку в этот банк через СНС: данные по заявке и клиенту возьму из карточки выбора и карточки клиента, " +
      "недостающее спрошу здесь. В СНС отправлю только после вашего подтверждения." + docsNote + reasons,
      [{ label: o.eligible ? "Завести через СНС" : "Всё равно завести", primary: o.eligible, run: () => beginSns() },
       { label: "Не сейчас", run: () => { snsRef.current = null; H().pushText("assistant", "Хорошо, заявку не завожу."); } }]);
  });

  const beginSns = () => H().withBusy("Готовлю заявку…", async () => {
    const f = snsRef.current;
    if (!f) return;
    if (!H().ensureCase) { H().pushText("assistant", "Не могу сохранить карточку выбора из диалога — воспользуйтесь формой «Подать в банк (СНС)»."); return; }
    try {
      f.ref = await H().ensureCase!(f.offer);
      for (const x of f.more || []) f.ref = await H().ensureCase!(x.offer);   // все банки — в одну карточку
      const st = await api.getSubmission(f.ref);
      const sub = st.data || {};
      if (sub.submitted_at) {
        snsRef.current = null;
        H().pushText("assistant", `Заявка по подбору №${f.ref} уже подана в СНС (заявка СНС №${sub.sns_order_id}, статус «${sub.sns_status || "—"}»). Повторно не отправляю.`);
        return;
      }
      f.products = st.products || {};
      setSnsOn(true);
      f.sub = { ...sub, target_bank_ids: [f.snsBankId, ...(f.more || []).map((x) => x.snsBankId)] };
      await api.putSubmission(f.ref, snsEditable(f.sub));     // фиксируем предзаполнение в заявке
      f.docs = await api.listSubmissionDocs(f.ref).catch(() => []);
      f.inSns = st.docTypesInSns || [];
      f.docLabels = st.docTypes || {};
      f.docMatch = st.docTypeMatch || {};
      const got = (st.fromCard || []).map((k) => SNS_FIELDS.find((x) => x.key === k)?.label).filter(Boolean);
      H().pushText("assistant", `Карточка выбора №${f.ref}: ${[f.offer, ...(f.more || []).map((x) => x.offer)]
        .map((o) => `${o.bank}${o.platform ? ` (${o.platform})` : ""}`).join(", ")}, ` +
        `${formatRubles(H().getDeal().amount || 0)}.` + (got.length ? ` Из карточки клиента взял: ${got.join(", ")}.` : "") +
        " Недостающее спрошу по одному полю; «отмена» — прервать.");
      askNextSns();
    } catch (e: any) {
      snsRef.current = null;
      H().pushText("assistant", `Не удалось подготовить заявку: ${e?.message || e}.`);
    }
  });

  const askNextSns = () => {
    const f = snsRef.current;
    if (!f) return;
    const field = f.editQueue ? f.editQueue.shift() || null : nextField(f.sub, f.asked);
    if (f.editQueue && !f.editQueue.length && !field) f.editQueue = undefined;
    if (field) {
      f.step = "field"; f.field = field; f.asked.add(field.key);
      const cur = f.sub[field.key];
      const text = cur
        ? `${field.label}: сейчас «${field.key === "date_end" ? fmtDate(cur) : cur}». Новое значение или «оставить».`
        : field.ask;
      const actions: ChatAction[] = [];
      if (cur) actions.push({ label: "Оставить", run: () => answerSns("оставить") });
      else if (!field.required) actions.push({ label: "Пропустить", run: () => answerSns("пропустить") });
      if (actions.length) H().pushActions(text, actions); else H().pushText("assistant", text);
      return;
    }
    const have = (code: string) => f.docs.some((d) => d.doc_type === code) || f.inSns.includes(code);
    if (!have("passport")) {
      f.step = "passport";
      H().pushText("assistant", "Прикрепите скан паспорта руководителя кнопкой 📎 — без него СНС заявку не примет. " +
        "Документы храню у себя только до окончания скоринга в СНС, потом удаляю.");
      return;
    }
    if (!f.askedDocs) {
      f.askedDocs = true; f.step = "docs";
      // Что СНС подставит сама, называем прямо: иначе брокер идёт искать файлы, которые
      // у банка уже есть, и это самый частый лишний шаг при повторной заявке (#613).
      const known = f.inSns.map((c) => f.docLabels[c] || c).filter(Boolean);
      const head = known.length
        ? `У клиента в СНС уже есть: ${known.join(", ")} — повторно прикреплять не нужно.`
        : "Паспорт есть.";
      H().pushActions(`${head} Если нужно, добавьте остальное (устав, решение о назначении, отчётность) через 📎 — или нажмите «Готово».`,
        [{ label: "Готово", primary: true, run: () => showSnsSummary() }]);
      return;
    }
    showSnsSummary();
  };

  const answerSns = async (text: string) => {
    const f = snsRef.current;
    const field = f?.field;
    if (!f || !field || !f.ref) return;
    if (/^оставить/i.test(text.trim()) && f.sub[field.key]) { askNextSns(); return; }
    if (isSkip(text)) {
      if (field.required) { H().pushText("assistant", `«${field.label}» — обязательное поле для СНС. ${field.ask}`); return; }
      askNextSns(); return;
    }
    const v = field.parse(text);
    if (v == null) { H().pushText("assistant", `${field.hint} ${field.ask}`); return; }
    f.sub[field.key] = v;
    await H().withBusy("Сохраняю…", async () => {
      try { await api.putSubmission(f.ref!, { [field.key]: v }); askNextSns(); }
      catch (e: any) { H().pushText("assistant", `Не удалось сохранить: ${e?.message || e}. Повторите значение.`); }
    });
  };

  const attachSns = (file: File, meta?: { year: number; quarter: number }) => H().withBusy("Загружаю документ…", async () => {
    const f = snsRef.current;
    if (!f?.ref) return;
    if (!SNS_EXT_RE.test(file.name)) {
      H().pushText("assistant", `«${file.name}»: СНС принимает только PDF, JPG, PNG, TIF и подписи SIG/P7S. Пришлите файл в одном из этих форматов.`);
      return;
    }
    let type = f.step === "passport" || f.backStep === "passport"
      ? "passport" : docTypeByName(file.name, f.docMatch);
    if (meta) type = meta.quarter === 4 ? "buh" : "buh_q";
    // Бухотчётность: СНС требует отчётный год и период — спросим перед загрузкой.
    if ((type === "buh" || type === "buh_q") && !meta) {
      f.pendingFile = file; f.backStep = f.step; f.step = "docmeta";
      const y = new Date().getFullYear();
      const pick = (yy: number, q: number) => () => { void answerDocMeta(`${yy} ${q}`); };
      H().pushActions(`«${file.name}» — бухотчётность. За какой год и период? Напишите, например, «${y - 1} год» или «${y}, полугодие».`,
        [{ label: `${y - 1} · год`, run: pick(y - 1, 4) }, { label: `${y} · I квартал`, run: pick(y, 1) },
         { label: `${y} · полугодие`, run: pick(y, 2) }, { label: `${y} · 9 месяцев`, run: pick(y, 3) }]);
      return;
    }
    try {
      const up = await api.uploadSubmissionDoc(f.ref, type, file, /\.(sig|p7s)$/i.test(file.name), meta);
      f.docs.push({ id: up?.data?.[0]?.id, file_name: file.name, doc_type: type, meta });
      H().pushText("assistant", `Загрузил «${file.name}» как «${f.docLabels[type] || type}»` +
        (meta ? ` за ${meta.year}, ${api.SNS_QUARTERS[meta.quarter]}.` : "."));
      if (type === "passport") {
        // Паспорт не того лица/организации (по имени файла против ЕГРЮЛ) — предупреждаем сразу.
        try {
          const w = ((await api.getSubmission(f.ref)).warnings || []).find((x) => x.file === file.name);
          if (w) H().pushText("assistant", `⚠ ${w.message}.`);
        } catch { /* предупреждение не критично */ }
      }
      if (f.step === "docs") {
        H().pushActions("Ещё документы — через 📎, или «Готово».", [{ label: "Готово", primary: true, run: () => showSnsSummary() }]);
      } else if (f.step === "confirm") {
        showSnsSummary();
      } else {
        askNextSns();
      }
    } catch (e: any) {
      H().pushText("assistant", `Не удалось загрузить документ: ${e?.message || e}.`);
    }
  });

  // Ответ на «за какой год и период»: «2025 год», «2026, полугодие», «2026 9 мес», «2026 1».
  const answerDocMeta = async (text: string) => {
    const f = snsRef.current;
    if (!f?.pendingFile) return;
    const year = Number((text.match(/(19|20)\d{2}/) || [])[0]);
    const t = text.toLowerCase().replace(String(year), " ");
    const quarter = /полугод|\b2\b/.test(t) ? 2 : /9\s*мес|девят|\b3\b/.test(t) ? 3
      : /год|\b4\b/.test(t) ? 4 : /1\s*кв|i\s*кв|перв|квартал|\b1\b/.test(t) ? 1 : 0;
    if (!year || !quarter) {
      H().pushText("assistant", "Не понял период. Нужны год и период: «2025 год», «2026, I квартал», «2026, полугодие» или «2026, 9 месяцев».");
      return;
    }
    const file = f.pendingFile;
    f.pendingFile = undefined; f.step = f.backStep || "docs"; f.backStep = undefined;
    await attachSns(file, { year, quarter });
  };

  const showSnsSummary = () => {
    const f = snsRef.current;
    if (!f) return;
    f.step = "confirm";
    const s = f.sub, c = H().getDeal();
    const req = [s.bank_bic && `БИК ${s.bank_bic}`, s.payment_account && `р/с ${s.payment_account}`,
      s.correspondent_account && `к/с ${s.correspondent_account}`].filter(Boolean).join(", ");
    const lines = [
      `${f.more?.length ? "Банки" : "Банк"}: ${[{ offer: f.offer, snsName: f.snsName }, ...(f.more || [])]
        .map((x) => `${x.offer.bank}${x.snsName !== x.offer.bank ? ` (в СНС — «${x.snsName}»)` : ""}`).join(", ")}`,
      `Продукт: ${f.products[String(s.product_id)] || "—"}`,
      `Сумма гарантии: ${formatRubles(c.amount || 0)}${c.law ? ` · ${lawLabel(c.law)}` : ""}${c.tenderNumber ? ` · РНТ ${c.tenderNumber}` : ""}`,
      `Срок: ${fmtDate(s.date_start)} — ${fmtDate(s.date_end)}`,
      `Контакт: ${s.contact_name || "—"}, ${s.contact_phone || "—"}${s.contact_email ? `, ${s.contact_email}` : ""}`,
      `Реквизиты: ${req || "не указаны"}`,
      `Документы: ${f.docs.length ? f.docs.map((d) => d.file_name + (d.meta?.year ? ` (${d.meta.year}, ${api.SNS_QUARTERS[d.meta.quarter || 4]})` : "")).join(", ") : "—"}`,
    ];
    H().pushActions(`Заявка готова к отправке в СНС (карточка выбора №${f.ref}):\n\n${lines.join("\n")}\n\n` +
      "Контакты и реквизиты сохранены в карточке клиента.",
      [{ label: "Отправить в СНС", primary: true, run: () => submitSns() },
       { label: "Исправить данные", run: () => { f.editQueue = [...SNS_FIELDS]; askNextSns(); } },
       { label: "Отменить", run: () => cancelSns() }]);
  };

  const cancelSns = () => {
    snsRef.current = null; setSnsOn(false);
    H().pushText("assistant", "Заведение заявки прервано. Введённые данные сохранены в карточке выбора и карточке клиента — можно вернуться позже.");
  };

  const pollSnsStatus = async (ref: string) => {
    for (let i = 0; i < 30; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const st = (await api.getOrderStatus(ref)).data;
        if (!st.inProgress) {
          const blockers = (st.blockers || []).map((b: any) => b?.message || b?.title || String(b)).filter(Boolean);
          H().pushText("assistant", `СНС: ${st.statusLabel || st.status}.` +
            (blockers.length ? `\nЗамечания: ${blockers.join("; ")}` : "") +
            "\nСкоринг в СНС завершён — переданные документы у нас удалены.");
          return;
        }
      } catch { /* временная ошибка опроса — пробуем дальше */ }
    }
    H().pushText("assistant", "СНС ещё проверяет заявку. Статус и решения банков — в разделе «Заявки».");
  };

  const submitSns = () => H().withBusy("Отправляю заявку в СНС…", async () => {
    const f = snsRef.current;
    if (!f?.ref) return;
    try {
      const r = (await api.submitToBank(f.ref)).data;
      f.step = "sent";
      snsRef.current = null; setSnsOn(false);
      const notes: string[] = [];
      const blockers = (r.blockers || []).map((b: any) => b?.message || b?.code).filter(Boolean);
      if (r.queued === false) notes.push("Заявка ждёт снятия замечаний и уйдёт в банк сама" + (blockers.length ? `: ${blockers.join("; ")}` : "."));
      for (const w of r.documentWarnings || []) notes.push(`${w.type} (${w.files.join(", ")}): ${w.message}.`);
      H().pushActions(`Заявка отправлена в СНС: №${r.orderId}, статус «${r.statusLabel || r.status}».` +
        (notes.length ? `\n${notes.join("\n")}` : "") +
        "\nИдёт распознавание документов и скоринг — сообщу результат. Дальше заявка — в разделе «Заявки»: решения банков, допзапросы и уведомления.",
        [{ label: "Открыть в «Заявки»", primary: true, run: () => api.openApplication(f.ref) }]);
      void pollSnsStatus(f.ref);
    } catch (e: any) {
      const msg = String(e?.message || e);
      const d = e?.detail || {};
      const actions: ChatAction[] = [];
      if (d.code === "DOCUMENTS_REJECTED") {
        // Непринятые СНС файлы убираем из заявки и даём приложить исправленные.
        const bad = new Set<string>((d.documents || []).flatMap((x: any) => x.files || []));
        actions.push({ label: "Убрать отклонённые и приложить заново", primary: true, run: () => H().withBusy("Убираю документы…", async () => {
          for (const doc of f.docs.filter((x) => bad.has(x.file_name) && x.id)) {
            try { await api.deleteSubmissionDoc(doc.id!); } catch { /* уже удалён */ }
          }
          f.docs = f.docs.filter((x) => !bad.has(x.file_name));
          f.askedDocs = false; askNextSns();
        }) });
      } else if (d.code !== "ORDER_NO_PRICED_BANK") {
        actions.push({ label: "Исправить данные", run: () => { f.editQueue = [...SNS_FIELDS]; askNextSns(); } });
      }
      actions.push({ label: "Отменить", run: () => cancelSns() });
      // У отказа с кодом текст уже человеческий (бэкенд разобрал код СНС) — без префикса.
      H().pushActions((d.code ? msg : `СНС не приняла заявку: ${msg}`) +
        (d.code === "ORDER_NO_PRICED_BANK" ? "\nВыберите в подборе другой банк или измените сумму и срок." : "") +
        (d.requestId ? `\n(номер запроса для поддержки СНС: ${d.requestId})` : ""), actions);
    }
  });

  // «Какие банки подключены к СНС / куда подать автоматически / а Сбербанк?» — живая
  // проверка по списку СНС (#436). LLM тут выдумывала подключение по площадкам реестра.
  const answerSnsList = (text: string, followUp: string | null) => H().withBusy("Проверяю подключение к СНС…", async () => {
    const offers = H().getOffers();
    const target = findSnsTarget(text, offers);
    const one = target ? target.offer.bank : followUp;
    try {
      if (!one && !offers.length) {
        const list = await api.snsBanks();
        H().pushText("assistant", list.length
          ? `К СНС подключены (${list.length}): ${list.map((b) => b.name).join(", ")}. Заявку в них можно завести и отправить отсюда — подберите банки и напишите «подай заявку в …».`
          : "Список банков СНС пуст.");
        return;
      }
      const fmtO = (o: BrokerOffer) => `площадка ${o.platform || "—"}, ${o.eligible ? "проходит" : "не проходит"}, ` +
        (o.commission != null ? `комиссия ${formatRubles(o.commission)}` : "цену назовёт СНС") +
        (o.avRate ? `, КВ ${Math.round(o.avRate * 100)}%` : "");
      if (one) {
        // Банк из подбора — по его предложениям (площадкам); нет в подборе — по банку целиком.
        const bankName = target?.offer.bank || findSnsTarget(one, offers)?.offer.bank || one;
        lastBankRef.current = bankName;
        const bankOffers = offers.filter((o) => o.bank === bankName);
        if (bankOffers.length) {
          const cache = await resolveOffers(bankOffers);
          const o = snsOffersOf(bankOffers, cache)[0];
          if (o) {
            const m = cache.get(keyOf(o))!;
            H().pushActions(`${o.bank} подключён к СНС${m.snsName && m.snsName !== o.bank ? ` («${m.snsName}»)` : ""} — ${fmtO(o)}. ` +
              "Заявку можно завести и отправить отсюда.",
              o.eligible ? [{ label: `Завести в ${o.bank}`, primary: true, run: () => { void startSns(o); } }] : []);
          } else {
            const plats = Array.from(new Set(bankOffers.map((x) => x.platform).filter(Boolean)));
            H().pushText("assistant", `${whyNot(cache.get(keyOf(bankOffers[0])), { bank: bankName, platform: bankOffers.length === 1 ? bankOffers[0].platform : null })}` +
              `.${plats.length ? ` Подать можно на площадках: ${plats.join(", ")}.` : ""}`);
          }
        } else {
          const [r] = await api.snsResolve([{ name: bankName }]);
          H().pushText("assistant", r?.connected
            ? `${bankName} подключён к СНС${r.platform ? ` (площадка ${r.platform})` : ""}, но в текущем подборе его нет.`
            : `${whyNot(r, { bank: bankName })}.`);
        }
        return;
      }
      const cache = await resolveOffers(offers);
      const on = snsOffersOf(offers, cache);
      const onBanks = new Set(on.map((o) => o.bank));
      const off = Array.from(new Set(offers.map((o) => o.bank))).filter((b) => !onBanks.has(b));
      if (!on.length) {
        H().pushText("assistant", `Из банков подбора через СНС не подаётся ни один (проверено ${off.length}) — только напрямую на площадках.`);
        return;
      }
      H().pushActions(`Через СНС подаются ${on.length} из ${onBanks.size + off.length} банков подбора — заявку в них можно завести и отправить отсюда:\n` +
        on.map((o) => `• ${o.bank} — ${fmtO(o)}`).join("\n") +
        (off.length ? `\n\nНе через СНС: ${off.slice(0, 12).join(", ")}${off.length > 12 ? ` и ещё ${off.length - 12}` : ""}.` : ""),
        on.filter((o) => o.eligible).slice(0, 4).map((o) => ({ label: `Завести в ${o.bank}`, run: () => { void startSns(o); } })));
    } catch (e: any) {
      H().pushText("assistant", e?.detail?.code === "SNS_NOT_CONFIGURED" ? e.message : `Не удалось проверить подключение к СНС: ${e?.message || e}.`);
    } finally {
      snsAskedRef.current = true;
    }
  });

  /** Заявка СНС сразу в несколько выбранных банков (#445, #446): одна заявка, несколько
   *  банков. Непроходящие по условиям — с причинами и «Всё равно завести». */
  const startSnsMany = (chosen: BrokerOffer[]) => H().withBusy("Проверяю банки СНС…", async () => {
    let cache: Map<string, api.SnsBankMatch>;
    try { cache = await resolveOffers(chosen); }
    catch (e: any) { H().pushText("assistant", e?.detail?.code === "SNS_NOT_CONFIGURED" ? e.message : `Не удалось проверить подключение к СНС: ${e?.message || e}.`); return; }
    const sns = chosen.filter((o) => cache.get(keyOf(o))?.connected);
    const skipped = chosen.filter((o) => !cache.get(keyOf(o))?.connected);
    if (!sns.length) { H().pushText("assistant", "Из выбранных банков через СНС не подаётся ни один."); return; }
    const bad = sns.filter((o) => !o.eligible);
    const pack = sns.map((o) => ({ offer: o, snsBankId: cache.get(keyOf(o))!.snsBankId!, snsName: cache.get(keyOf(o))!.snsName || o.bank }));
    lastBankRef.current = null;
    snsRef.current = {
      offer: pack[0].offer, snsBankId: pack[0].snsBankId, snsName: pack[0].snsName, more: pack.slice(1),
      sub: {}, products: {}, asked: new Set(), docs: [], inSns: [], docLabels: {},
      docMatch: {}, askedDocs: false, step: "offer",
    };
    const docsNoteMany = await snsDocsNote(H().getDeal().inn);
    H().pushActions(`Одна заявка через СНС сразу в ${sns.length} банк(ов):\n` +
      sns.map((o) => `• ${o.bank} — площадка ${o.platform}, ${o.commission != null ? `комиссия ${formatRubles(o.commission)}` : "цену назовёт СНС"}` +
        (o.eligible ? "" : ` ⚠️ не проходит: ${(o.reasons || []).join("; ") || "причина не указана"}`)).join("\n") +
      (skipped.length ? `\n\nНе включаю (через СНС не подаются): ${skipped.map((o) => o.bank).join(", ")}.` : "") +
      (bad.length ? "\n\nБанки с ⚠️ по условиям скорее всего откажут." : "") + docsNoteMany +
      "\n\nДанные возьму из карточки выбора и карточки клиента, недостающее спрошу здесь; в СНС отправлю только после подтверждения.",
      [{ label: bad.length ? `Всё равно завести в ${sns.length} банк(ов)` : `Завести в ${sns.length} банк(ов)`, primary: !bad.length, run: () => beginSns() },
       { label: "Не сейчас", run: () => { snsRef.current = null; H().pushText("assistant", "Хорошо, заявку не завожу."); } }]);
  });

  /** «Отправь заявку во все банки СНС»: список банков СНС из подбора с чекбоксами (#446). */
  const startSnsAll = () => H().withBusy("Проверяю банки СНС…", async () => {
    const offers = H().getOffers();
    if (!offers.length) { H().pushText("assistant", "Сначала подберём банки — заявку заведу по предложениям подбора."); return; }
    let all: BrokerOffer[];
    try { all = snsOffersOf(offers, await resolveOffers(offers)); }
    catch (e: any) { H().pushText("assistant", e?.detail?.code === "SNS_NOT_CONFIGURED" ? e.message : `Не удалось проверить подключение к СНС: ${e?.message || e}.`); return; }
    if (!all.length) { H().pushText("assistant", "Из банков подбора через СНС не подаётся ни один."); return; }
    const ok = all.filter((o) => o.eligible);
    if (H().pushSnsPicker) {
      H().pushSnsPicker!(`Банки СНС из подбора: ${all.length}, проходят по условиям — ${ok.length} (отмечены). ` +
        "Снимите лишние галочки или добавьте нужные и нажмите «Подать в СНС» — заявка уйдёт одной во все отмеченные.", all);
      return;
    }
    if (!ok.length) { H().pushText("assistant", `Банки СНС из подбора по условиям не проходят: ${all.map((o) => o.bank).join(", ")}.`); return; }
    await startSnsMany(ok);
  });

  /** Подать в выбранные чекбоксами (#446): один — сценарий на банк, несколько — одна заявка. */
  const submitChosen = (chosen: BrokerOffer[]) => (chosen.length === 1 ? startSns(chosen[0]) : startSnsMany(chosen));

  /** «Почему не проходит Сбербанк?», «причины» — причины из подбора по СНС-предложению банка. */
  const answerReasons = (text: string) => H().withBusy("Смотрю причины…", async () => {
    const offers = H().getOffers();
    const bank = findSnsTarget(text, offers)?.offer.bank || lastBankRef.current;
    if (!bank) { H().pushText("assistant", "По какому банку показать причины? Назовите банк из подбора."); return; }
    lastBankRef.current = bank;
    const bankOffers = offers.filter((o) => o.bank === bank);
    if (!bankOffers.length) { H().pushText("assistant", `${bank} нет в текущем подборе.`); return; }
    let sns: BrokerOffer | undefined;
    try { sns = snsOffersOf(bankOffers, await resolveOffers(bankOffers))[0]; } catch { /* без СНС — по всем площадкам */ }
    const show = sns ? [sns] : bankOffers;
    H().pushText("assistant", show.map((o) => o.eligible
      ? `${o.bank} (${o.platform || "—"}${sns ? ", канал СНС" : ""}) — проходит по условиям.`
      : `${o.bank} (${o.platform || "—"}${sns ? ", канал СНС" : ""}) не проходит:\n${(o.reasons || []).map((r) => `• ${r}`).join("\n") || "• причина не указана"}`).join("\n\n"));
    snsAskedRef.current = true;                  // дальше можно «а Реалист?»
  });

  // Намерение «подай заявку в …» → банк/площадка из подбора (или банк, о котором шла речь).
  /** Добавить к начатой заявке банки, которые остались от того же выбора (#615).
   *
   *  Кейс выбора один, `ensureCase` кладёт предложения в него же — поэтому «добавить»
   *  означает перезапустить сбор с расширенным составом, а не завести вторую заявку. */
  const addMoreBanks = async () => {
    const rest = restOfPickRef.current;
    if (!rest.length) {
      H().pushText("assistant", "Больше подходящих банков в этом подборе нет — все уже в заявке.");
      return;
    }
    const all = [...pickedRef.current, ...rest];
    restOfPickRef.current = [];
    pickedRef.current = all;
    H().pushText("assistant", `Добавляю: ${rest.map((o) => o.bank).join(", ")}.`);
    await startSnsMany(all);
  };

  const handleSnsIntent = async (text: string) => {
    const offers = H().getOffers();
    if (!offers.length) { H().pushText("assistant", "Сначала подберём банки — заявку заведу по выбранному предложению."); return; }
    // «в самые дешёвые банки», «в топ-3», «во все проходящие» — банк не назван, но выбор
    // однозначен. Раньше такая просьба уходила в модель, и та отвечала инструкцией
    // «подготовьте заявку» вместо того, чтобы её завести (#615).
    const bulk = bulkSnsPick(text);
    if (bulk && !findSnsTarget(text, offers)) {
      if (bulk.kind === "all") { await startSnsAll(); return; }
      const pool = offers.filter((o) => o.eligible);
      const byBank = new Map<string, BrokerOffer>();
      for (const o of pool) {
        const cur = byBank.get(o.bank);
        const better = bulk.kind === "kv"
          ? (o.avRate || 0) > (cur?.avRate || 0)
          : (o.commission || Infinity) < (cur?.commission || Infinity);
        if (!cur || better) byBank.set(o.bank, o);
      }
      const sorted = Array.from(byBank.values()).sort((a, b) => bulk.kind === "kv"
        ? (b.avRate || 0) - (a.avRate || 0)
        : (a.commission || Infinity) - (b.commission || Infinity));
      const chosen = sorted.slice(0, bulk.limit);
      if (!chosen.length) { H().pushText("assistant", "Среди проходящих банков подходящих под этот запрос нет."); return; }
      restOfPickRef.current = sorted.slice(bulk.limit);
      pickedRef.current = chosen;
      await startSnsMany(chosen);
      return;
    }
    let target = findSnsTarget(text, offers);
    if (!target) {
      const said = H().getSaidTexts();
      const o = lastMentionedOffer(said, offers);
      if (o) target = { offer: o, byPlatform: false };
    }
    if (!target) { H().pushText("assistant", "В какой банк подать заявку? Назовите банк или площадку из подбора."); return; }
    await startSns(target.offer, target.byPlatform, !mentionsPlatform(text, offers));
  };


  /** Идёт сбор данных заявки: ответы и 📎 уходят в заявку. true — сообщение обработано. */
  const handleInFlow = async (text: string, file: File | null): Promise<boolean> => {
    const flow = snsRef.current;
    if (!flow?.ref) return false;
    if (file) { await attachSns(file); return true; }
    if (isCancel(text)) { cancelSns(); return true; }
    // Вопрос посреди сбора («а какой банк дешевле?») — не ответ на поле: отвечаем как обычно.
    if (flow.step === "field" && !/[?？]/.test(text)) { await answerSns(text); return true; }
    if (flow.step === "docmeta") { await answerDocMeta(text); return true; }
    if (flow.step === "docs" && /^(готово|вс[её]|дальше|нет)/i.test(text)) { showSnsSummary(); return true; }
    if (flow.step === "confirm" && /^(отправ|да|подтверждаю)/i.test(text)) { await submitSns(); return true; }
    return false;                                // иное — обычный вопрос; заявка остаётся открытой
  };

  /** «Какие банки подключены к СНС», «а Сбербанк?», «подай заявку в …». true — обработано. */
  const handleIntents = async (text: string): Promise<boolean> => {
    const offers = H().getOffers();
    const wasSns = snsAskedRef.current;
    snsAskedRef.current = false;
    // «почему не проходит / причины» — причины отказа, а не статус подключения.
    if (/почему|причин|не\s*проход|не\s*подход/i.test(text) && (wasSns || /снс/i.test(text) || findSnsTarget(text, offers))) {
      await answerReasons(text); return true;
    }
    // «отправь заявку во все банки СНС» — одна заявка в несколько банков.
    if (isSnsSubmitIntent(text) && /(во|в|по)\s+все[хм]?\s+банк|всем\s+банкам/i.test(text)) { await startSnsAll(); return true; }
    // Уточнение «а Сбербанк?» — только если это банк из подбора (иначе «причины» стали бы банком).
    const followRaw = wasSns ? followUpBankName(text) : null;
    const follow = followRaw && findSnsTarget(followRaw, offers) ? followRaw : null;
    if (isSnsListIntent(text) || follow) { await answerSnsList(text, follow); return true; }
    if (isSnsSubmitIntent(text)) {
      lastSubmitTextRef.current = text;
      await handleSnsIntent(text); return true;
    }
    // «так подготовь мне» — повтор последней просьбы о подаче с тем же условием.
    if (isDoItIntent(text) && lastSubmitTextRef.current) {
      await handleSnsIntent(lastSubmitTextRef.current); return true;
    }
    // «добавь ещё банки» — расширяем уже выбранный состав остатком того же подбора.
    if (isAddBanksIntent(text) && (restOfPickRef.current.length || pickedRef.current.length)) {
      await addMoreBanks(); return true;
    }
    return false;
  };

  /** Подпись реплики пользователя для вложения во время сбора заявки. */
  const fileLabel = (file: File | null) => (snsRef.current?.ref && file ? `📎 ${file.name}` : null);

  return { snsOn, startSns, submitChosen, handleInFlow, handleIntents, fileLabel, resolveConnected };
}
